import React, { useState } from 'react';
import { Car, MapPin, Calendar, Clock, Phone, CheckCircle, Star, Shield, ChevronRight } from 'lucide-react';
import './CarRentals.css';

const TFN = '+18447356222';
const TFN_DISPLAY = '+1 (844) 735-6222';

function CarRentals() {
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    pickupLocation: '',
    dropoffLocation: '',
    pickupDate: today,
    pickupTime: '10:00',
    dropoffDate: '',
    dropoffTime: '10:00',
    sameReturn: true,
    driverAge: '25+',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const carCategories = [
    { name: 'Economy', icon: '🚗', desc: 'Great fuel economy for city trips', price: '$29/day', img: 'https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?w=600' },
    { name: 'SUV', icon: '🚙', desc: 'Spacious comfort for families', price: '$59/day', img: 'https://images.unsplash.com/photo-1606016159991-dfe4f2746ad5?w=600' },
    { name: 'Luxury', icon: '🏎️', desc: 'Premium vehicles for special occasions', price: '$99/day', img: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600' },
    { name: 'Van / Minivan', icon: '🚐', desc: 'Perfect for groups & road trips', price: '$79/day', img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600' },
  ];

  const brands = [
    { name: 'Hertz', logo: '🚘' },
    { name: 'Enterprise', logo: '🚖' },
    { name: 'Avis', logo: '🚗' },
    { name: 'Budget', logo: '🚕' },
    { name: 'National', logo: '🚙' },
    { name: 'Alamo', logo: '🚘' },
  ];

  const features = [
    { icon: <Shield size={28} />, title: 'Full Insurance Coverage', desc: 'Drive with peace of mind — all rentals include comprehensive coverage options.' },
    { icon: <CheckCircle size={28} />, title: 'No Hidden Fees', desc: 'Transparent pricing with no surprise charges at pickup.' },
    { icon: <Car size={28} />, title: '500+ Car Types', desc: 'Economy, SUV, luxury, vans & more from top global brands.' },
    { icon: <Clock size={28} />, title: '24/7 Roadside Assist', desc: 'Our team is always a call away if you need help on the road.' },
  ];

  return (
    <div className="car-rentals-page">
      {/* Hero */}
      <div className="car-hero">
        <div className="car-hero-overlay" />
        <div className="car-hero-content">
          <span className="car-hero-badge"><Car size={16} /> Car Rentals</span>
          <h1>Find Your Perfect Rental Car</h1>
          <p>Compare hundreds of cars from top rental companies. Best prices guaranteed.</p>
          <a href={`tel:${TFN}`} className="car-hero-cta">
            <Phone size={20} /> Call {TFN_DISPLAY} to Book
          </a>
        </div>
      </div>

      {/* Search Form */}
      <div className="car-search-wrapper">
        <div className="car-search-box">
          <h2><Car size={22} /> Search Car Rentals</h2>

          {submitted ? (
            <div className="car-search-result">
              <div className="car-call-card">
                <div className="car-call-icon"><Phone size={40} /></div>
                <h3>Ready to Book Your Car?</h3>
                <p>Our travel experts have access to exclusive car rental deals not available online. Call us now for the best rates!</p>
                <a href={`tel:${TFN}`} className="car-call-btn">
                  <Phone size={20} /> {TFN_DISPLAY}
                </a>
                <p className="car-call-sub">Available 24/7 · No hold times · Instant confirmation</p>
                <button className="car-back-btn" onClick={() => setSubmitted(false)}>← Modify Search</button>
              </div>
            </div>
          ) : (
            <form className="car-search-form" onSubmit={handleSearch}>
              <div className="car-form-row">
                <div className="car-form-group">
                  <label><MapPin size={15} /> Pick-up Location</label>
                  <input
                    type="text"
                    name="pickupLocation"
                    value={form.pickupLocation}
                    onChange={handleChange}
                    placeholder="City, airport or address"
                    required
                  />
                </div>
                {!form.sameReturn && (
                  <div className="car-form-group">
                    <label><MapPin size={15} /> Drop-off Location</label>
                    <input
                      type="text"
                      name="dropoffLocation"
                      value={form.dropoffLocation}
                      onChange={handleChange}
                      placeholder="City, airport or address"
                    />
                  </div>
                )}
              </div>

              <div className="car-same-return">
                <label className="car-checkbox-label">
                  <input
                    type="checkbox"
                    name="sameReturn"
                    checked={form.sameReturn}
                    onChange={handleChange}
                  />
                  Return to same location
                </label>
              </div>

              <div className="car-form-row">
                <div className="car-form-group">
                  <label><Calendar size={15} /> Pick-up Date</label>
                  <input
                    type="date"
                    name="pickupDate"
                    value={form.pickupDate}
                    min={today}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="car-form-group">
                  <label><Clock size={15} /> Pick-up Time</label>
                  <select name="pickupTime" value={form.pickupTime} onChange={handleChange}>
                    {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="car-form-group">
                  <label><Calendar size={15} /> Drop-off Date</label>
                  <input
                    type="date"
                    name="dropoffDate"
                    value={form.dropoffDate}
                    min={form.pickupDate || today}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="car-form-group">
                  <label><Clock size={15} /> Drop-off Time</label>
                  <select name="dropoffTime" value={form.dropoffTime} onChange={handleChange}>
                    {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="car-form-row car-form-row-bottom">
                <div className="car-form-group car-age-group">
                  <label>Driver Age</label>
                  <select name="driverAge" value={form.driverAge} onChange={handleChange}>
                    <option>18-24</option>
                    <option>25+</option>
                  </select>
                </div>
                <button type="submit" className="car-search-btn">
                  <Car size={18} /> Search Cars
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Car Categories */}
      <section className="car-categories-section">
        <div className="car-section-inner">
          <h2 className="car-section-title">Browse by Vehicle Type</h2>
          <p className="car-section-sub">From budget-friendly economy cars to spacious SUVs and luxury rides</p>
          <div className="car-categories-grid">
            {carCategories.map((cat, i) => (
              <div className="car-category-card" key={i}>
                <div className="car-category-img" style={{ backgroundImage: `url(${cat.img})` }} />
                <div className="car-category-body">
                  <div className="car-category-icon">{cat.icon}</div>
                  <h3>{cat.name}</h3>
                  <p>{cat.desc}</p>
                  <div className="car-category-price">From {cat.price}</div>
                  <a href={`tel:${TFN}`} className="car-category-btn">
                    Book Now <ChevronRight size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="car-features-section">
        <div className="car-section-inner">
          <h2 className="car-section-title">Why Book With Us?</h2>
          <div className="car-features-grid">
            {features.map((f, i) => (
              <div className="car-feature-card" key={i}>
                <div className="car-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="car-brands-section">
        <div className="car-section-inner">
          <h2 className="car-section-title">Top Rental Partners</h2>
          <div className="car-brands-grid">
            {brands.map((b, i) => (
              <div className="car-brand-chip" key={i}>
                <span>{b.logo}</span> {b.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="car-cta-section">
        <div className="car-cta-inner">
          <div className="car-cta-text">
            <h2>Get Exclusive Car Rental Deals</h2>
            <p>Our agents have access to unpublished rates. Call now for instant pricing and confirmation.</p>
          </div>
          <a href={`tel:${TFN}`} className="car-cta-btn">
            <Phone size={22} /> {TFN_DISPLAY}
          </a>
        </div>
      </section>

      {/* Reviews */}
      <section className="car-reviews-section">
        <div className="car-section-inner">
          <h2 className="car-section-title">What Customers Say</h2>
          <div className="car-reviews-grid">
            {[
              { name: 'Jessica M.', stars: 5, text: 'Got a luxury SUV at half the price I found online. The agent was incredibly helpful!' },
              { name: 'David K.', stars: 5, text: 'Quick booking, no hidden fees, and the car was exactly what I needed for a cross-country road trip.' },
              { name: 'Sarah L.', stars: 5, text: 'Called in the morning and had a confirmation within minutes. Amazing service!' },
            ].map((r, i) => (
              <div className="car-review-card" key={i}>
                <div className="car-review-stars">
                  {[...Array(r.stars)].map((_, j) => <Star key={j} size={16} fill="#f6ad55" color="#f6ad55" />)}
                </div>
                <p>"{r.text}"</p>
                <strong>{r.name}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CarRentals;
